import React, { useState } from 'react';
import { EInvoice } from '../types';
import { API_BASE as BASE } from '../services/apiBase';
import { proveriOdgovor } from '../services/greska';

interface Props {
  code: string;
  invoices: EInvoice[];
  onSaved: () => void;
}

/**
 * E-fakture: prodaja ustanovama, van kase.
 *
 * Ovde se ne bira odakle roba ide. Ovde se odgovara na pitanje koje kod kase i
 * ne postoji: <b>vozimo li je uopšte</b>.
 *
 * Kod kase se to zna samo od sebe — mesto kupca se popunjava samo kad se roba
 * vozi. Na e-fakturi adresa stoji uvek, jer je to sedište ustanove i ide na sam
 * dokument. Zato adresa ovde ništa ne dokazuje, a pita se poslovođa: on zna da
 * li ustanova šalje svoje vozilo ili čeka naše.
 *
 * Jedan izuzetak ne čeka nikoga: kad je isporuka ili montaža <b>naplaćena</b>.
 * To niko ne plaća za robu koju nosi sam.
 */
export default function EInvoicePanel({ code, invoices, onSaved }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const odluci = async (documentKey: string, deliver: boolean) => {
    setBusy(documentKey);
    try {
      const putanja = `${BASE}/api/store/${code}/e-fakture/`
        + `${encodeURIComponent(documentKey)}/odluka`;

      const response = await fetch(putanja, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deliver, note: null })
      });

      await proveriOdgovor(response, `/api/store/${code}/e-fakture`);

      setError(null);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (invoices.length === 0) {
    return <p className="empty">Nema e-faktura koje čekaju odluku.</p>;
  }

  const cekaju = invoices.filter((i) => i.needsDecision);

  /**
   * „Ustanova preuzima sama" stoji odvojeno, sklopljeno.
   *
   * Do 23.09.2026. je taj red nestajao u trenutku klika i nije se pojavljivao
   * nigde — ni operateru. Jedan promašen red je značio isporuku koju aplikacija
   * više nikad ne prikaže. A to je najčešći potez na ovom ekranu: od 31 odluke
   * 30 je bilo „preuzimaju sami".
   *
   * Sklopljeno zato što je to završen posao; otvara se samo kad nekog treba
   * vratiti.
   */
  const preuzimaju = invoices.filter((i) => i.takenByCustomer);
  const posao = invoices.filter((i) => !i.takenByCustomer);

  return (
    <>
      <p className="note">
        Ovo su fakture ustanovama koje si ti otkucao. Za razliku od računa sa
        kase, ovde se ne vidi da li robu vozimo mi ili je ustanova preuzima
        sama — <strong>zato pitamo tebe</strong>. Dok ne kažeš, isporuka ne
        postoji ni za koga.
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <ul className="sourcing">
        {posao.map((invoice) => (
          <li key={invoice.documentKey}>
            <p className="sourcing__head">
              {invoice.customer}
              <span className="muted">
                {' '}· {invoice.address}, {invoice.city}
                {' '}· čeka {invoice.waitingDays} d
              </span>
            </p>

            <ul className="sourcing__items">
              {invoice.items.map((item) => (
                <li key={item.articleCode}>
                  <span className="sourcing__what">
                    {item.articleName} <span className="muted">× {item.quantity}</span>
                  </span>
                </li>
              ))}
            </ul>

            {/* Naplacena isporuka govori sama za sebe. */}
            {invoice.deliveryWasCharged ? (
              <p className="sourcing__reserved">
                Isporuka je naplaćena — ovo vozimo, ništa se ne pita.
              </p>
            ) : invoice.needsDecision ? (
              <p className="disc__action">
                <button
                  type="button"
                  className="primary"
                  disabled={busy === invoice.documentKey}
                  onClick={() => void odluci(invoice.documentKey, true)}
                >
                  {busy === invoice.documentKey ? 'Upisujem…' : 'Vozimo mi'}
                </button>
                {' '}
                <button
                  type="button"
                  className="link link--danger"
                  disabled={busy === invoice.documentKey}
                  onClick={() => void odluci(invoice.documentKey, false)}
                >
                  Ustanova preuzima sama
                </button>
              </p>
            ) : (
              <p className="sourcing__reserved">
                Vozimo — rekao {invoice.decidedBy}
                {invoice.decidedAt
                  && ` ${new Date(invoice.decidedAt).toLocaleDateString('sr-RS')}`}.
                {' '}
                <button
                  type="button"
                  className="link link--danger"
                  disabled={busy === invoice.documentKey}
                  onClick={() => void odluci(invoice.documentKey, false)}
                >
                  ipak ne vozimo
                </button>
              </p>
            )}
          </li>
        ))}
      </ul>

      {cekaju.length === 0 && (
        <p className="muted">Sve je odlučeno. Nove fakture će se pojaviti ovde same.</p>
      )}

      {preuzimaju.length > 0 && (
        <details className="details">
          <summary>Ustanova preuzima sama ({preuzimaju.length})</summary>

          <p className="note">
            Ovo su fakture za koje si rekao da ih ne vozimo. Stoje ovde da bi se
            mogle vratiti ako je red promašen — dok su ovde, nijedna tura ih ne
            vidi.
          </p>

          <ul className="sourcing">
            {preuzimaju.map((invoice) => (
              <li key={invoice.documentKey}>
                <p className="sourcing__head">
                  {invoice.customer}
                  <span className="muted">
                    {' '}· {invoice.address}, {invoice.city}
                    {invoice.decidedBy && ` · rekao ${invoice.decidedBy}`}
                    {invoice.decidedAt
                      && ` ${new Date(invoice.decidedAt).toLocaleDateString('sr-RS')}`}
                  </span>
                </p>

                <p className="disc__action">
                  <button
                    type="button"
                    className="link"
                    disabled={busy === invoice.documentKey}
                    onClick={() => void odluci(invoice.documentKey, true)}
                  >
                    {busy === invoice.documentKey ? 'Upisujem…' : 'ipak vozimo mi'}
                  </button>
                </p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}
